//@ts-nocheck
"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { generateHexUrl, unHexString } from "../../utils/hexes";
import styles from "./show-all-results.module.css";
import SearchModal from "../search-modal/search-modal";
import { useState, useEffect, useMemo, useCallback } from "react";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { FormatDateNoUTC } from "../../utils/date-formatter";
import { GoDotFill } from "react-icons/go";
import Link from "next/link";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";
import { MdSkipNext, MdSkipPrevious } from "react-icons/md";
import { VscSparkleFilled } from "react-icons/vsc";
import { fetchSummary } from "../../utils/fetchSummary";
import NarrativeSlider from "../narrative-slider/narrative-slider";
import { vxiContentInterpreter, WhatsNew } from "../../utils/vxi-content-interpreter";
import clsx from "clsx";
import CTABtn from "../atoms/cta-btn";
import FilterDropdownList from "../atoms/filter-dropdown-list";
import { slugBuilder } from "../../utils/slug-builder";
import { TbLoaderQuarter } from "react-icons/tb";

export default function ShowAllResults({ posts, vxiContent, cxaHubs, mostSearched }) {
    const searchParams = useSearchParams();
    const hexValue = searchParams.get("in");
    const [searchResults, setSearchResults] = useState(null);
    const [inputValue, setInputValue] = useState(hexValue ? unHexString(hexValue) : "");
    const [currentPage, setCurrentPage] = useState(1);
    const [showBottomPagination, setShowBottomPagination] = useState(false);
    const [showList, setShowList] = useState(true);
    const [formData, setFormData] = useState(null);
    const [loadingItem, setLoadingItem] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const router = useRouter();

    async function fetchData(value: string | null) {
        if (!value) return;
        setInputValue(unHexString(value));
    }

    useEffect(() => {
        fetchData(hexValue);
        setLoadingItem(null);
        //console.log("try rerender")
    }, [hexValue]);



    const itemsPerPage = 10;

    // Build dropdown options from the current smart-search results.
    // Each shaped result carries: sectionTitle, typeTitle, topics[], industries[], solutions[]
    function buildDropdownList(results = []) {
        const topics = new Set();
        const industries = new Set();
        const solutions = new Set();
        const sections = new Set(); // section + type live under same dropdown

        results.forEach(r => {
            if (!r || typeof r !== 'object') return;

            const pushAll = (set, arr) => {
                (arr || []).forEach(v => {
                    const title = typeof v === 'string' ? v : (v?.title || v?.name);
                    if (title) set.add(title);
                });
            };

            pushAll(topics, r.topics);
            pushAll(industries, r.industries);
            pushAll(solutions, r.solutions);

            if (r.sectionTitle) sections.add(r.sectionTitle);
            if (r.typeTitle) sections.add(r.typeTitle);
        });

        return [
            {
                key: 'topics',
                name: 'Topic',
                type: 'list',
                values: [...topics].map(t => ({ title: t, value: t }))
            },
            {
                key: 'industries',
                name: 'Industry',
                type: 'list',
                values: [...industries].map(i => ({ title: i, value: i }))
            },
            {
                key: 'solutions',
                name: 'Solution',
                type: 'list',
                values: [...solutions].map(s => ({ title: s, value: s }))
            },
            {
                key: 'sections',
                name: 'Type',
                type: 'list',
                values: [...sections].map(sec => ({ title: sec, value: sec }))
            }
        ];
    }

    const dropdownList = useMemo(
        () => buildDropdownList(searchResults?.results || []),
        [searchResults]
    );
    const transformedList = useMemo(
        () => dropdownList.map(item => {
            if (item.type === 'list') {
                return {
                    type: item.type,
                    name: item.name,
                    key: item.key,
                    itemData: {
                        [item.name]: item.values.map(val => ({
                            attributes: { title: val.title, key: item.key, value: val.value }
                        }))
                    }
                };
            }
            return item;
        }),
        [dropdownList]
    );
    //console.log(transformedList);
    const handleDropdownChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value || null })); // update first
        //console.log(formData)
        setShowList(false);
        setTimeout(() => setShowList(true), 120);
    };

    const showMoreResults = useMemo(() => {
        if (!searchResults?.results) return [];

        const totalItemsToShow = itemsPerPage * currentPage;

        return searchResults.results
            .filter((res) => {
                try {
                    return new URL(res.link).origin !== window.location.origin;
                } catch {
                    return true;
                }
            })
            .slice(0, totalItemsToShow); // show more items as currentPage increases
    }, [searchResults, currentPage]);

    const combinedData = useMemo(
        () => [...(cxaHubs || []), ...(posts || []), ...(vxiContent || [])],
        [cxaHubs, posts, vxiContent]
    );

    const filteredShowMoreResults = useMemo(() => {
        if (!showMoreResults?.length) return [];

        const filterTopic = formData?.topics?.trim();
        const filterIndustry = formData?.industries;
        const filterSolution = formData?.solutions;
        const filterSection = formData?.sections;

        const titleSet = (arr) => new Set(
            (arr || []).map(v => (typeof v === 'string' ? v : (v?.title || v?.name))).filter(Boolean)
        );

        return showMoreResults.filter(r => {
            if (!r) return false;

            const topics = titleSet(r.topics);
            const industries = titleSet(r.industries);
            const solutions = titleSet(r.solutions);

            const matchTopic = !filterTopic || topics.has(filterTopic);
            const matchIndustry = !filterIndustry || industries.has(filterIndustry);
            const matchSolution = !filterSolution || solutions.has(filterSolution);
            const matchSection =
                !filterSection ||
                r.sectionTitle === filterSection ||
                r.typeTitle === filterSection;

            return matchTopic && matchIndustry && matchSolution && matchSection;
        });
    }, [showMoreResults, formData]);

    useEffect(() => {
        setShowList(false);
        setTimeout(() => setShowList(true), 120);
    }, [searchResults]);

    const scrollToTop = useCallback(() => window.scrollTo({ top: 0, behavior: "smooth" }), []);

    useEffect(() => {
        const handleScroll = () => {
            const { scrollY, innerHeight } = window;
            const { scrollHeight } = document.body;
            setShowBottomPagination((scrollY + innerHeight) / scrollHeight >= 0.55);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const totalResults = searchResults?.results?.length || 0;
    const totalPages = Math.ceil(totalResults / itemsPerPage);

    const paginatedResults = useMemo(() => {
        if (!searchResults?.results) return [];
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return searchResults.results
            .filter((res) => {
                try {
                    return new URL(res.link).origin !== window.location.origin;
                } catch {
                    return true;
                }
            })
            .slice(start, end);
    }, [searchResults, currentPage]);


    const Pagination = ({ visible }) =>
        totalPages > 1 && (
            <div className={styles.pagination} style={{ opacity: visible ? 1 : 0 }}>
                <button onClick={() => { setCurrentPage(1); scrollToTop(); }} disabled={currentPage === 1}>
                    <MdSkipPrevious />
                </button>
                <button onClick={() => { setCurrentPage(p => Math.max(p - 1, 1)); scrollToTop(); }} disabled={currentPage === 1}>
                    <GrFormPrevious />
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button onClick={() => { setCurrentPage(p => Math.min(p + 1, totalPages)); scrollToTop(); }} disabled={currentPage === totalPages}>
                    <GrFormNext />
                </button>
                <button onClick={() => { setCurrentPage(totalPages); scrollToTop(); }} disabled={currentPage === totalPages}>
                    <MdSkipNext />
                </button>
            </div>
        );

    const showMoreButton = useMemo(() => {
        if (totalPages > 1) {
            setCurrentPage(currentPage + 1);
        }
    }, [showBottomPagination]);

    const fnCatch = (value) => {
        // console.log("[smart-search] fnCatch payload:", value);
        setSearchResults(value);
        setCurrentPage(1);
    };
    const [newData, setNewData] = useState([]);
    useEffect(() => {
        const loadWhatsNew = async () => {
            try {
                const res = await fetch("/api/whats-new", { cache: "no-store" });
                const json = await res.json();
                const list = Array.isArray(json) ? json : json?.data ?? [];
                const interpreted = list.map((item) => {
                    const record = item?.data ?? item;
                    return vxiContentInterpreter({
                        ...record?.attributes,
                        id: record?.id,
                        source: item?.source,
                        path: item?.path,
                    });
                });
                setNewData(interpreted);
            } catch (err) {
                console.error("[whats-new] fetch error:", err);
            }
        };
        loadWhatsNew();
    }, []);

    // .sort((a, b) => new Date(b.attributes.publishDate) - new Date(a.attributes.publishDate))
    // .slice(0, 4)
    // .map((item) => {
    //     // console.log(item);
    //     return vxiContentInterpreter({ id: item.id, ...item.attributes });
    // });

    // useEffect(() => {
    //     console.log(newData)
    // }, [newData]);

    return (
        <div className={styles.container}>
            <div className={styles["hidden-search"]}>
                <SearchModal fnCatch={fnCatch} cxaHubs={cxaHubs} posts={posts} vxiContent={vxiContent} inputValue={inputValue} loading={searchLoading} setLoading={setSearchLoading} />
            </div>

            <div className={styles["main-search"]}>
                <FaMagnifyingGlass size={32} />
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Search..."
                />
            </div>

            {totalResults > 0 && currentPage === 1 && (
                <small>
                    {totalResults < 10
                        ? totalResults
                        : `${Math.floor(searchResults?.results.length / 10) * 10}${searchResults?.results.length % 10 === 0 ? "" : "+"}`}{" "}
                    {totalResults === 1 ? "result" : "results"} found.
                </small>
            )}

            {totalResults > 0 && currentPage !== 1 && (
                <small>
                    Showing {filteredShowMoreResults?.length} out of {totalResults}.
                </small>
            )}

            {/* {mostSearched && <div className={styles["most-searched"]}>
                <div className="sectionName">Most searched</div>
                <div>
                    {mostSearched.slice(0, 5).map((m, i) => (
                        <div className={styles["most-searched-item"]} key={i}>
                            {loadingItem === m.item && (
                                <div style={{ marginTop: 4 }}>
                                    <TbLoaderQuarter
                                        size={18}
                                        color="#ff6d10"
                                        className={styles.spinner}
                                    />
                                </div>
                            )}
                            <Link href={generateHexUrl(m.item)}
                                onClick={(e) => {
                                    if (unHexString(hexValue) === m.item) {
                                        e.preventDefault();
                                        return;
                                    }
                                    setLoadingItem(m.item);
                                }}
                            >
                                {m.item}
                            </Link>
                        </div>
                    ))}
                </div>
            </div>} */}

            {!searchLoading && filteredShowMoreResults?.length !== 0 && <div className={styles.filters}>
                <div className="sectionName">Sort results by</div>
                <div>
                    {transformedList && transformedList.map((item, i) => {
                        const autoKey = item.key;                 // example: "topics"
                        const autoValue = formData?.[autoKey] ?? null; // example: "AI"
                        const optionCount = item?.itemData?.[item.name]?.length ?? 0;

                        if (optionCount === 0) return null;

                        return (
                            <div key={i} className={styles["filter-holder"]}>
                                <FilterDropdownList
                                    item={item.itemData}
                                    mode="drop&go"
                                    setValue={(key, value) => handleDropdownChange(key, value)}
                                    autoload={autoValue ? [autoKey, autoValue] : []}
                                />
                            </div>
                        );
                    })}

                </div>
            </div>}

            {/* <Pagination visible={!showBottomPagination} /> */}

            {!searchLoading && filteredShowMoreResults?.length !== 0 && showList && <div className={styles.results}>
                {filteredShowMoreResults?.map((res, i) => (
                    <SearchResultItem
                        key={i}
                        result={res}
                        searchInput={searchResults?.input}
                        posts={posts}
                        vxiContent={vxiContent}
                        cxaHubs={cxaHubs}
                    />
                ))}
            </div>}

            {!searchLoading && filteredShowMoreResults?.length === 0 && <div className={clsx(styles["results-header"], "titles-h2-bold")} style={{ gap: 28 }}>
                <div><span>Looks like we don’t have anything on&nbsp;</span><i style={{ fontWeight: 300 }}>"{inputValue}"</i><span>&nbsp;{!formData && "just yet."}</span>
                    {formData && <span style={{ display: 'flex', alignItems: 'center' }}>
                        {"with"}&nbsp;
                        <span style={{ display: 'flex', gap: 14 }}>
                            {formData && Object.entries(formData).map(([key, value], i) => {

                                const match = transformedList.find(item =>
                                    item.key === key
                                );
                                if (!match) return null;

                                return (
                                    value && <div key={i} className={styles["filter-holder"]}>
                                        <FilterDropdownList
                                            item={match.itemData}
                                            mode="drop&go"
                                            setValue={(key, value) => handleDropdownChange(key, value)}
                                            autoload={[`${key}`, `${value}`]}
                                        />
                                    </div>
                                );
                            })}
                        </span>
                        &nbsp;{"just yet."}
                    </span>}
                </div>
                <div style={{ fontWeight: 300, fontSize: '15pt', lineHeight: '21pt' }}><span>Let’s change that — reach out and let’s start the conversation.&nbsp;</span></div>
                <div style={{ lineHeight: '21pt', marginBlock: '28pt' }}><CTABtn btnName={"Contact Us"} btnURL={"/contact-us"} /></div>
            </div>}
            {searchLoading &&
                <div>Searching...</div>
            }
            {/* <Pagination visible={showBottomPagination} /> */}

            <NarrativeSlider title="You may also like" vxi_contents={newData} />
        </div>
    );
}

/* --------------------------------------
   Subcomponent: Each search result item
---------------------------------------*/
function SearchResultItem({ result, searchInput, posts, vxiContent, cxaHubs }) {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const titleMatch = result?.title?.trim().toLowerCase();
    const matched =
        posts.find((p) => p.attributes.title?.trim().toLowerCase() === titleMatch) ||
        vxiContent.find((v) => v.attributes.title?.trim().toLowerCase() === titleMatch)
        || cxaHubs.find((c) => c.title?.trim().toLowerCase() === titleMatch);

    const sourceData = {
        id: matched?.id,
        attributes: {
            title: matched?.attributes.title,
            menuTitle: matched?.attributes.menuTitle,
            subMenuTitle: matched?.attributes.subMenuTitle,
            type: matched?.attributes.type?.title,
        },
    };

    // Flatten section components
    if (Array.isArray(matched?.attributes.section)) {
        let counter = {
            floorPanel: 1,
            sliderList: 1,
            metrics: 1,
            announcements: 1,
            borderedItemList: 1,
            leadersList: 1,
            section: 1,
        };

        matched?.attributes.section.forEach((section) => {
            // Generic fields
            if (section.title) sourceData.attributes[`sectionTitle${counter.section}`] = section.title;
            if (section.description) sourceData.attributes[`sectionDescription${counter.section}`] = section.description;
            if (section.sectionName) sourceData.attributes[`sectionName${counter.section}`] = section.sectionName;
            counter.section++;

            // Features Elevator
            if (Array.isArray(section.floorPanel)) {
                section.floorPanel.forEach((panel) => {
                    const key = `floorPanel${counter.floorPanel}`;
                    sourceData.attributes[`${key}Title`] = panel.title;
                    sourceData.attributes[`${key}Description`] = panel.description;
                    if (panel.imageCaption) sourceData.attributes[`${key}ImageCaption`] = panel.imageCaption;
                    counter.floorPanel++;
                });
            }

            // Numbers and Metrics
            if (Array.isArray(section.metrics)) {
                section.metrics.forEach((metric) => {
                    const key = `metric${counter.metrics}`;
                    sourceData.attributes[`${key}Description`] = metric.metricDescription;
                    sourceData.attributes[`${key}Value`] = metric.metricValue;
                    counter.metrics++;
                });
            }

            // Narrative Slider
            if (Array.isArray(section.sliderList)) {
                section.sliderList.forEach((slider) => {
                    const key = `slider${counter.sliderList}`;
                    sourceData.attributes[`${key}Title`] = slider.sliderTitle;
                    sourceData.attributes[`${key}Description`] = slider.description;
                    counter.sliderList++;
                });
            }

            // Announcements
            if (Array.isArray(section.announcements)) {
                section.announcements.forEach((ann) => {
                    const key = `announcement${counter.announcements}`;
                    sourceData.attributes[`${key}Title`] = ann.title;
                    sourceData.attributes[`${key}Description`] = ann.description;
                    if (ann.sectionName) sourceData.attributes[`${key}SectionName`] = ann.sectionName;
                    counter.announcements++;
                });
            }

            // Bordered Item List
            if (Array.isArray(section.borderedItemList)) {
                section.borderedItemList.forEach((item) => {
                    const key = `borderedItem${counter.borderedItemList}`;
                    sourceData.attributes[`${key}Title`] = item.title;
                    sourceData.attributes[`${key}Description`] = item.description;
                    counter.borderedItemList++;
                });
            }

            // Leaders
            if (Array.isArray(section.leadersList)) {
                section.leadersList.forEach((leader) => {
                    const key = `leader${counter.leadersList}`;
                    sourceData.attributes[`${key}Name`] = leader.name;
                    sourceData.attributes[`${key}Description`] = leader.description;
                    if (leader.bio) sourceData.attributes[`${key}Bio`] = leader.bio;
                    counter.leadersList++;
                });
            }
        });
    }

    // Flatten vxiContent
    if (matched?.attributes.vxiContent) {
        const vxi = matched?.attributes.vxiContent;
        if (vxi.heroDescription) sourceData.attributes.heroDescription = vxi.heroDescription;
        if (vxi.mainContent) sourceData.attributes.mainContent = vxi.mainContent.replace(/<[^>]*>/g, "").trim();
        if (vxi.author) {
            if (vxi.author.authorFullName) sourceData.attributes.authorFullName = vxi.author.authorFullName;
            if (vxi.author.authorTitle) sourceData.attributes.authorTitle = vxi.author.authorTitle;
            if (vxi.author.authorBiography) sourceData.attributes.authorBiography = vxi.author.authorBiography;
        }
    }

    // Flatten solutions
    if (Array.isArray(matched?.attributes.solutions?.data)) {
        matched?.attributes.solutions.data.forEach((s, idx) => {
            sourceData.attributes[`solution${idx + 1}`] = s.attributes.title;
        });
    }

    // Flatten industries
    if (Array.isArray(matched?.attributes.industries?.data)) {
        matched?.attributes.industries.data.forEach((i, idx) => {
            sourceData.attributes[`industry${idx + 1}`] = i.attributes.title;
        });
    }

    const handleSummaryClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Toggle off if already open
        if (expanded) return setExpanded(false);

        setLoading(true);
        setError(null);

        try {
            // ✅ use the helper we built
            // console.log(sourceData, searchInput);
            const summaryText = await fetchSummary({ sourceData, searchInput });

            setSummary(summaryText || "No summary available.");
            setExpanded(true);
        } catch (err) {
            console.error(err);
            setError("Failed to fetch summary.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.resultItem}>
            <div title={result.title}>
                <div className={styles.item}>
                    <div className={clsx("sectionName", styles.crumbs)}>
                        {(() => {
                            const crumbs = result?.crumbs || "";
                            const parts = crumbs.split(">").map(p => p.trim());

                            // Case 1: no ">"
                            if (parts.length === 1) {
                                return (
                                    <Link href={`/what-we-think/?Types=${crumbs}`}>
                                        {crumbs}
                                    </Link>
                                );
                            }

                            // Case 2: has ">"
                            const [parent, child] = parts;

                            return (
                                <div>
                                    <Link href={`/${slugBuilder(parent)}`}>{parent}</Link>
                                    <span>{" > "}</span>
                                    {child === "CXA Hub" ? <Link href={`/${slugBuilder(parent, child)}`}>{child}</Link> : <span>{child}</span>}
                                </div>
                            );
                        })()}
                    </div>
                    <Link href={result.link} className={styles.title}>{result.title}</Link>
                    <div className={clsx("sectionName", styles["date-published"])}>
                        {result?.type}{" "}
                        {result?.type && <GoDotFill size={10} />}{" "}
                        {result.date && FormatDateNoUTC(result.date)}
                    </div>
                </div>
            </div>
        </div>
    );
}

